import { Calendar, ChevronLeft, ChevronRight, Info, RotateCcw, Zap } from "lucide-react";
import { formatarDataLongaBr, formatarDataCurtaBr, diasUteisAte } from "../../utils/dateBr";
import { fmtBRL, fmtBRLcompactSigned, fmtMWh, fmtMWm } from "../../utils/formatters";
import { EstimativaBadge } from "../ui/EstimativaBadge";
import { usePosicaoAberta } from "../../hooks/usePosicaoAberta";
import { PosicaoBadge } from "./PosicaoBadge";
import type { SeisDuInfo } from "../../utils/mockData";
import type { FonteCalendario6du } from "../../api/types";

interface Props {
  info: SeisDuInfo;
  /** Task #61: indica que `info.data` veio da heurística do 6du em vez
   * do calendário CCEE oficial. Quando true, mostra `<EstimativaBadge>`. */
  isEstimativa?: boolean;
  /** Fonte da data do 6du exibido (oficial, estimado gravado, heurística ou
   * proxy). 16/10: escolhe o tooltip do `<EstimativaBadge>`. */
  fonte?: FonteCalendario6du;
  /** Fix #156: false quando canonical não tem trade pra `info.data` (=
   * `info.entradas` e `info.saidas` valem 0 sintéticos). Card mostra
   * "—" cinza no lugar de R$ 0,00 — empty state honesto, sem mock leak. */
  temDadosReais?: boolean;
  /** Task #170: navegação prev/next entre 6dus. Quando ausente, o Card
   * renderiza só o 6du de `info.data` (comportamento legado). */
  nav?: SixDuNav;
  /** 05/08: de onde vieram entradas/saídas — "calendario_live" (fonte única)
   * ou "fluxo_legado" (fallback do precompute; NUNCA silencioso — a page loga
   * warn quando vira permanente por erro do calendário). Vira `data-fonte` no
   * root pra observabilidade/testes. */
  fonteDados?: "calendario_live" | "fluxo_legado";
  /** Task #206: mes_ref ("YYYY-MM-DD", dia 1 do fornecimento) do 6du
   * exibido — alimenta o KPI "Posição aberta" via `usePosicaoAberta`.
   *  - `string`    → busca a posição daquele mês (varia ao navegar).
   *  - `undefined` → default do endpoint (próximo 6du oficial; legado).
   *  - `null`      → 6du histórico: KPI mostra "—" sem fetch.
   * Sem a prop o KPI congelava no próximo oficial em toda navegação. */
  mesRef?: string | null;
}

export interface SixDuNav {
  /** Relação de `info.data` com o próximo 6du oficial e com hoje. */
  relacao: "historico" | "proximo" | "futuro";
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onVoltarProximo: () => void;
}

const POSICAO_ABERTA_TOOLTIP =
  "MtM estimado: volume líquido aberto × preço efetivo da curva " +
  "forward para o 6du de referência. Representa o impacto de zerar " +
  "a posição hoje no mercado spot (SHORT vai comprar = saída de " +
  "caixa; LONG vai vender = entrada). NÃO confundir com valor " +
  "contratado bruto (somatório compras − vendas já fechadas) — " +
  "métricas diferentes por design.";

// Task #161: o Card mostra só o DIA da liquidação CCEE (1 dia); a Tabela
// "Saldo projetado 6 meses" mostra o MÊS inteiro. Tooltip + subtítulo
// deixam o escopo explícito pra não parecer divergência/bug.
const ESCOPO_TOOLTIP =
  "Valores referem-se APENAS ao dia da liquidação CCEE (6º dia útil). " +
  "Para o mês inteiro, ver Tabela 'Saldo projetado 6 meses' abaixo.";

export function SixDuCard({ info, isEstimativa = false, fonte, temDadosReais = true, nav, mesRef, fonteDados }: Props) {
  const hoje = new Date();
  // Task #170: rótulo de dias úteis depende da relação com hoje.
  //  - próximo/futuro: "faltam X dias úteis" (contagem hoje → data)
  //  - histórico:      "liquidou há X dias úteis" (contagem data → hoje)
  const isHistorico = nav?.relacao === "historico";
  const dias = isHistorico ? diasUteisAte(hoje, info.data) : diasUteisAte(info.data, hoje);
  const diasLabelPrefix = isHistorico ? "liquidou há" : "faltam";
  const isProximoOficial = nav === undefined || nav.relacao === "proximo";

  // Task #72/#75: posicao aberta vem do mesmo hook que a Section. Em
  // loading/erro/sem dados, valor = 0 e o KPI mostra traco.
  // Task #83: KPI agora exibe MtM (volume × curva forward) em vez de
  // posicao_valor_estimado (saldo R$ contratado, dupla contagem com
  // Entradas/Saidas bilaterais ja somadas no Fluxo).
  const posAberta = usePosicaoAberta(mesRef);
  const posAbertaValor = Number(posAberta.data?.total_valor_mtm_estimado ?? "0");
  const posAbertaTemDados =
    !posAberta.isLoading && (posAberta.data?.linhas_posicao_aberta?.length ?? 0) > 0;
  const posPositivo = posAbertaValor >= 0;
  // Task #80: volume agregado MWh/MWm vem do response do mesmo endpoint —
  // soma server-side ja respeita o filtro do default (apenas mes_alvo).
  const posVolumeMwh = posAberta.data?.total_volume_mwh ?? null;
  const posVolumeMwm = posAberta.data?.total_volume_mwm ?? null;
  // Task #83: quantas linhas ficaram sem MtM por falta de curva forward.
  const linhasSemMtm = posAberta.data?.linhas_sem_mtm ?? 0;

  // Task #75 + #83: saldo total calculado com MtM da posicao aberta.
  // Fix #156: quando !temDadosReais, entradas/saidas valem 0 (empty state) —
  // saldo total fica = posicao aberta MtM apenas. Card NAO renderiza
  // R$ 0,00 inventado: saldo total tambem mostra "—" se nao houver pos.
  const saldoTotalEstimado =
    info.entradas - info.saidas + (posAbertaTemDados ? posAbertaValor : 0);
  const totalPositivo = saldoTotalEstimado >= 0;
  const temAlgumDado = temDadosReais || posAbertaTemDados;
  const dataCurta = formatarDataCurtaBr(info.data);
  const tooltipSemDados = `Aguardando lançamentos no canonical para ${dataCurta}.`;

  return (
    <div
      className="rounded-2xl border border-indra-turquesa/30 bg-gradient-to-br from-indra-turquesa-muted to-indra-azul-surface p-6 mb-6"
      data-testid="sixdu-card"
      data-fonte={fonteDados}
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 text-indra-turquesa text-xs tracking-wider font-semibold">
            <Zap size={14} />
            <span>Próximo 6DU — Liquidação CCEE</span>
            <span
              className="cursor-help"
              title={ESCOPO_TOOLTIP}
              data-testid="sixdu-escopo-tooltip"
            >
              <Info size={12} className="text-indra-turquesa/70" />
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-3 flex-wrap">
            {nav && (
              <button
                type="button"
                onClick={nav.onPrev}
                disabled={!nav.canPrev}
                data-testid="sixdu-prev"
                aria-label="6DU anterior"
                title="6DU anterior"
                className="text-white/60 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed self-center"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <h2
              className="text-2xl md:text-3xl font-black text-white font-sans"
              data-testid="sixdu-data"
            >
              {formatarDataLongaBr(info.data)}
            </h2>
            {nav && (
              <button
                type="button"
                onClick={nav.onNext}
                disabled={!nav.canNext}
                data-testid="sixdu-next"
                aria-label="Próximo 6DU"
                title="Próximo 6DU"
                className="text-white/60 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed self-center"
              >
                <ChevronRight size={20} />
              </button>
            )}
            <div className="flex items-center gap-1 text-white/60 text-sm">
              <Calendar size={14} />
              <span data-testid="sixdu-dias-label">
                {diasLabelPrefix}{" "}
                <span className="text-indra-turquesa font-semibold">{dias}</span>{" "}
                {dias === 1 ? "dia útil" : "dias úteis"}
              </span>
            </div>
            {isEstimativa && <EstimativaBadge ano={info.data.getFullYear()} fonte={fonte} />}
          </div>

          {/* Task #161: escopo do Card = 1 dia (vs Tabela 6m = mês inteiro). */}
          <div
            className="mt-1 text-sm font-medium tracking-wider text-white/55"
            data-testid="sixdu-escopo-subtitulo"
          >
            1 DIA · LIQUIDAÇÃO CCEE
          </div>

          {nav && !isProximoOficial && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <span
                data-testid="sixdu-badge-relacao"
                className={
                  "inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded " +
                  (nav.relacao === "historico"
                    ? "bg-white/10 text-white/60"
                    : "bg-amber-500/15 text-amber-300")
                }
              >
                {nav.relacao === "historico" ? "Histórico" : "Futuro"}
              </span>
              <button
                type="button"
                onClick={nav.onVoltarProximo}
                data-testid="sixdu-voltar-proximo"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-indra-turquesa hover:text-indra-turquesa/80"
              >
                <RotateCcw size={12} />
                Voltar ao próximo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Task #75: 4 KPIs no MESMO nivel visual.
          [Entradas] [Saidas] [Posicao Aberta - estimativa] [Saldo Total Estimado].
          Saldo Total destacado com border indra-turquesa, igual o ex-"Saldo
          Liquido 6du" tinha — agora carrega o valor final relevante. */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className="bg-indra-azul-deep/60 rounded-xl p-4 border border-white/5"
          data-testid="sixdu-kpi-entradas"
        >
          <div className="text-[11px] tracking-wider text-white/60">
            Entradas previstas
          </div>
          <div
            className={
              "mt-1 text-2xl font-black font-sans " +
              (temDadosReais ? "text-indra-positivo" : "text-white/40")
            }
            title={temDadosReais ? fmtBRL(info.entradas, { decimals: 2 }) : tooltipSemDados}
            data-testid="sixdu-entradas-valor"
          >
            {temDadosReais ? fmtBRL(info.entradas, { abbreviate: true }) : "—"}
          </div>
        </div>

        <div
          className="bg-indra-azul-deep/60 rounded-xl p-4 border border-white/5"
          data-testid="sixdu-kpi-saidas"
        >
          <div className="text-[11px] tracking-wider text-white/60">
            Saídas previstas
          </div>
          <div
            className={
              "mt-1 text-2xl font-black font-sans " +
              (temDadosReais ? "text-indra-negativo" : "text-white/40")
            }
            title={temDadosReais ? fmtBRL(info.saidas, { decimals: 2 }) : tooltipSemDados}
            data-testid="sixdu-saidas-valor"
          >
            {temDadosReais ? fmtBRL(info.saidas, { abbreviate: true }) : "—"}
          </div>
        </div>

        <div
          className="bg-indra-azul-deep/60 rounded-xl p-4 border border-amber-300/20"
          data-testid="sixdu-kpi-posicao-aberta"
        >
          <div className="text-[11px] tracking-wider text-white/60 flex items-center gap-1 flex-wrap">
            Posição aberta
            <span
              className="text-amber-200/70 text-[10px] uppercase tracking-wider"
              data-testid="sixdu-posicao-aberta-selo"
            >
              estimativa
            </span>
            <span
              className="cursor-help"
              title={POSICAO_ABERTA_TOOLTIP}
              data-testid="sixdu-posicao-aberta-tooltip"
            >
              <Info size={11} className="text-amber-200/70" />
            </span>
          </div>
          <div
            className={
              "mt-1 text-2xl font-black font-sans " +
              (posAbertaTemDados
                ? posPositivo
                  ? "text-indra-positivo"
                  : "text-indra-negativo"
                : "text-white/40")
            }
            data-testid="sixdu-posicao-aberta-mtm"
          >
            {/* UX 03/07 item 7: sinal tipografico do formatador (+/− unicode),
                nao "+" na mao; valor abreviado como os demais KPIs. */}
            {posAbertaTemDados ? fmtBRLcompactSigned(posAbertaValor) : "—"}
          </div>
          {/* Task #83: aviso quando ha linhas sem MtM (sem janela de
              curva forward cobrindo). Total do KPI exclui essas linhas;
              UI deixa explicito pra Mesa nao confundir com 0 real. */}
          {posAbertaTemDados && linhasSemMtm > 0 && (
            <div
              className="mt-0.5 text-[10px] text-amber-200/70"
              data-testid="sixdu-posicao-aberta-sem-mtm"
              title="Linhas sem janela de curva forward cobrindo o mes — MtM nao foi calculado pra elas."
            >
              {linhasSemMtm} {linhasSemMtm === 1 ? "linha sem curva" : "linhas sem curva"}
            </div>
          )}
          {/* Task #80b: 3a linha com badge LONG/SHORT/FLAT + MWh · MWm
              horizontal, ~70% do tamanho do R$. Cor cascateada do sinal
              do VOLUME (nao do R$) — caso raro de divergencia (trade
              indexado com val_total descolado do volume), badge e
              numero contam a verdade fisica. Sumida sem dados ou
              FLAT zerado — KPI nao polui. */}
          {posAbertaTemDados &&
            (Number(posVolumeMwh ?? "0") !== 0 || Number(posVolumeMwm ?? "0") !== 0) && (
              <div
                className={
                  "mt-0.5 flex items-center gap-1.5 text-sm font-mono opacity-90 " +
                  (Number(posVolumeMwh ?? "0") > 0
                    ? "text-indra-positivo"
                    : Number(posVolumeMwh ?? "0") < 0
                      ? "text-indra-negativo"
                      : "text-white/60")
                }
                data-testid="sixdu-posicao-aberta-volume"
              >
                <PosicaoBadge
                  volumeMwh={posVolumeMwh}
                  testIdPrefix="sixdu-posicao-aberta-badge"
                />
                <span>
                  {fmtMWh(posVolumeMwh)} · {fmtMWm(posVolumeMwm)}
                </span>
              </div>
            )}
        </div>

        <div
          className={
            "rounded-xl p-4 border " +
            (!temAlgumDado
              ? "bg-indra-azul-deep/60 border-white/5"
              : totalPositivo
                ? "bg-indra-turquesa-muted border-indra-turquesa/40"
                : "bg-indra-negativo/10 border-indra-negativo/40")
          }
          data-testid="sixdu-kpi-saldo-total"
        >
          <div className="text-[11px] tracking-wider text-white/60">
            Saldo total estimado
          </div>
          <div
            className={
              "mt-1 text-2xl font-black font-sans " +
              (!temAlgumDado
                ? "text-white/40"
                : totalPositivo
                  ? "text-indra-turquesa"
                  : "text-indra-negativo")
            }
            title={!temAlgumDado ? tooltipSemDados : fmtBRL(saldoTotalEstimado, { decimals: 2 })}
            data-testid="sixdu-saldo-total"
          >
            {!temAlgumDado ? "—" : fmtBRLcompactSigned(saldoTotalEstimado)}
          </div>
        </div>
      </div>
    </div>
  );
}
